import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { GET_TASKS_BY_PROJECT_QUERY, UPDATE_TASK_MUTATION, DELETE_TASK_MUTATION, ADD_SUBTASK_MUTATION } from '../query/gqlQueries'
import s from './SubtaskList.module.scss'

// A task's subtasks as a checklist, under its description (as the Work app
// shows them): tick one to mark it done (it moves to Done on the board), add
// one, or take one away. Each is a whole task on the same project, so the
// board's cards count them (0/5) and the iPad and TV show the same list.
// A task still being added has no id yet: its subtasks are listed here
// (`pending`) and made as soon as the task is saved (EditTaskDialog).
export default function SubtaskList({ task, pending, onPendingChange }) {
  const project = task?.properties?.project
  const q = useQuery(GET_TASKS_BY_PROJECT_QUERY, { variables: { projectName: project }, skip: !project, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const [update] = useMutation(UPDATE_TASK_MUTATION)
  const [remove] = useMutation(DELETE_TASK_MUTATION)
  const [add, { loading: adding }] = useMutation(ADD_SUBTASK_MUTATION)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(null)
  const [confirm, setConfirm] = useState(null)
  const [err, setErr] = useState('')

  if (!task?.identity && onPendingChange) {
    const addPending = () => { const t = draft.trim(); if (t) { onPendingChange([...(pending || []), t]); setDraft('') } }
    return (
      <div className={s.wrap}>
        <div className={s.head}><span>Subtasks</span>{pending?.length > 0 && <span className={s.count}>{pending.length}</span>}</div>
        <ul className={s.list}>
          {(pending || []).map((t, i) => (
            <li key={i} className={s.row}>
              <span className={s.tick} aria-hidden />
              <span className={s.title}>{t}</span>
              <button type="button" className={s.remove} onClick={() => onPendingChange(pending.filter((_, j) => j !== i))} aria-label="Remove subtask">✕</button>
            </li>
          ))}
        </ul>
        <div className={s.addRow}>
          <input className={s.input} value={draft} placeholder="Add a subtask…" onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPending() } }} />
          <button type="button" className={s.addBtn} onClick={addPending} disabled={!draft.trim()}>Add</button>
        </div>
        {pending?.length > 0 && <div className={s.note}>Made when you save the task.</div>}
      </div>
    )
  }
  if (!task?.identity || task.properties?.parentId) return null
  const subs = (q.data?.getTasksByProject || [])
    .filter(item => item.task.properties.parentId === task.identity)
    .sort((a, b) => (a.task.properties.subtaskOrder ?? 0) - (b.task.properties.subtaskOrder ?? 0))
  const done = subs.filter(item => item.status === 'DONE').length

  const act = async (id, fn) => {
    setBusy(id); setErr('')
    try { await fn(); await q.refetch() } catch (e) { setErr(e.message || 'That didn’t save.') }
    setBusy(null)
  }
  const toggle = (item) => act(item.task.identity, () => update({
    variables: { identity: item.task.identity, input: { position: item.status === 'DONE' ? 'Backlog' : 'Done' } },
  }))
  const addOne = () => {
    const title = draft.trim()
    if (!title) return
    act('new', async () => { await add({ variables: { parentId: task.identity, title } }); setDraft('') })
  }
  const removeOne = (item) => act(item.task.identity, async () => { await remove({ variables: { identity: item.task.identity } }); setConfirm(null) })

  return (
    <div className={s.wrap}>
      <div className={s.head}>
        <span>Subtasks</span>
        {subs.length > 0 && <span className={s.count}>{done}/{subs.length}</span>}
      </div>
      {subs.length > 0 && (
        <div className={s.bar}><span style={{ width: `${(done / subs.length) * 100}%` }} className={done === subs.length ? s.barDone : ''} /></div>
      )}
      <ul className={s.list}>
        {subs.map(item => {
          const isDone = item.status === 'DONE'
          const id = item.task.identity
          return (
            <li key={id} className={`${s.row} ${isDone ? s.done : ''}`}>
              <button type="button" className={s.tick} onClick={() => toggle(item)} disabled={busy === id} aria-label={isDone ? 'Mark not done' : 'Mark done'}>
                {isDone ? '✓' : ''}
              </button>
              <span className={s.title}>{item.task.properties.title}</span>
              {Number(item.task.properties.timeBudget) > 0 && <span className={s.hours}>{item.task.properties.timeBudget} h</span>}
              {confirm === id
                ? <span className={s.confirm}>Remove? <button type="button" onClick={() => removeOne(item)}>Yes</button><button type="button" onClick={() => setConfirm(null)}>No</button></span>
                : <button type="button" className={s.remove} onClick={() => setConfirm(id)} aria-label="Remove subtask">✕</button>}
            </li>
          )
        })}
      </ul>
      <div className={s.addRow}>
        <input className={s.input} value={draft} placeholder="Add a subtask…" onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOne() } }} />
        <button type="button" className={s.addBtn} onClick={addOne} disabled={!draft.trim() || adding}>Add</button>
      </div>
      {err && <div className={s.err}>{err}</div>}
    </div>
  )
}
